import { useCallback, useEffect, useMemo, useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import { shouldOfferInStockFilter } from "@models/productFacet";
import { toggled } from "@utils/toggled";
import { BottomSheet } from "./BottomSheet";
import { FilterPanel } from "./FilterPanel";
import { useSearchFilters, type SearchFilters } from "./useSearchFilters";
import { isFilterSheetOpenAtom } from "./store/sortFilterAtoms";
import {
    productFacetsAtom,
    productFacetsStatusAtom,
} from "@/store/productFacetAtoms";

interface FilterSheetProps {
    /** False on a category page, where the route fixes the category (#401 B4). */
    showCategories?: boolean;
    /** False on a vendor page, where the route fixes the vendor (#425). */
    showVendors?: boolean;
}

// Mobile-only Filter sheet. Selections are held in local state until Apply,
// which commits them to the URL via useSearchFilters.
export const FilterSheet = ({
    showCategories = true,
    showVendors = true,
}: FilterSheetProps = {}) => {
    const [isOpen, setIsOpen] = useAtom(isFilterSheetOpenAtom);
    // Same leak, same close: see SortSheet (#401 review R6).
    useEffect(() => () => setIsOpen(false), [setIsOpen]);
    if (!isOpen) return null;
    return (
        <FilterSheetContent
            showCategories={showCategories}
            showVendors={showVendors}
        />
    );
};

// What the sheet buffers: every filter dimension EXCEPT the two it does not offer a control for.
// Derived from SearchFilters by subtraction rather than listed, so the draft and the commit cannot
// disagree about which dimensions the sheet owns.
type FilterDraft = Omit<SearchFilters, "sort" | "amounts">;

const FilterSheetContent = ({
    showCategories,
    showVendors,
}: Required<FilterSheetProps>) => {
    const [, setIsOpen] = useAtom(isFilterSheetOpenAtom);
    const filters = useSearchFilters();
    const facets = useAtomValue(productFacetsAtom);
    const facetsStatus = useAtomValue(productFacetsStatusAtom);
    // Deliberately the COMMITTED inStockOnly, not the buffered one below: keyed on the buffer,
    // switching the toggle off would unmount the toggle mid-edit and leave no way to switch it
    // back on before Apply.
    const showInStockOnly = shouldOfferInStockFilter(
        facetsStatus,
        filters.inStockOnly,
    );
    // ONE buffered draft, not five parallel useStates (#396 / #226 review F10). The commit was
    // always whole — filters.setAll takes the full SearchFilters — so what the single object adds
    // is that the SEED must list every dimension too: FilterDraft is SearchFilters minus the two
    // the sheet does not edit, and a new dimension fails to compile in the seed until it is there.
    //
    // Seeded from the URL, and RE-seeded whenever the URL's filters change underneath the open
    // sheet (review F5): a browser Back does not close the sheet, and a draft still holding the
    // previous URL's values would re-write them on Apply and undo the Back. Nothing written to
    // the draft reaches useSearchFilters until Apply, which is what makes Close a discard.
    const { categoryIds, maxPrice, brands, vendorIds, inStockOnly } = filters;
    const seed = useMemo<FilterDraft>(
        () => ({ categoryIds, maxPrice, brands, vendorIds, inStockOnly }),
        [categoryIds, maxPrice, brands, vendorIds, inStockOnly],
    );
    const [draft, setDraft] = useState<FilterDraft>(seed);
    // The same reference on mount, so the first run is a no-op; a new one only when the URL was
    // re-parsed, which is a navigation — nothing else writes the URL while the sheet is up.
    useEffect(() => {
        setDraft(seed);
    }, [seed]);

    // Per-dimension updaters over the one draft. The chip toggles take the functional form
    // because the next value derives from the previous draft; the price cap and the in-stock
    // toggle are absolute values and set them straight in. The `Local` in the two setters' names
    // is load-bearing: `filters.setMaxPrice` and `filters.setInStockOnly` are in scope with the
    // same signatures and WRITE THE URL, and wiring a control to one of those instead would
    // typecheck clean while breaking the buffer contract (review F7).
    const toggleCategory = (id: number) =>
        setDraft((prev) => ({
            ...prev,
            categoryIds: toggled(prev.categoryIds, id),
        }));
    const toggleBrand = (value: string) =>
        setDraft((prev) => ({ ...prev, brands: toggled(prev.brands, value) }));
    const toggleVendor = (id: number) =>
        setDraft((prev) => ({
            ...prev,
            vendorIds: toggled(prev.vendorIds, id),
        }));
    const setLocalMaxPrice = (value: number | null) =>
        setDraft({ ...draft, maxPrice: value });
    const setLocalInStockOnly = (value: boolean) =>
        setDraft({ ...draft, inStockOnly: value });

    // Memoized because BottomSheet keys its keydown effect on `onClose`. A fresh arrow per render
    // tore that effect down and rebuilt it on every buffered edit — harmless now that the focus
    // trap and the scroll lock no longer share it (#226 review F5), but rebinding a window
    // listener on every slider stop is pure waste, and the coupling is exactly what made the
    // slider undrivable by keyboard before.
    const close = useCallback(() => setIsOpen(false), [setIsOpen]);
    const apply = () => {
        filters.setAll({
            // The two dimensions this sheet does not edit are carried through from the URL, not
            // cleared — Apply must not silently drop a filter it never offered to edit. Amount is
            // hidden for MVP (#226) but still lives in SearchFilters.
            sort: filters.sort,
            amounts: filters.amounts,
            // Everything the sheet DOES edit commits as one object: the whole buffer, or none of
            // it. A new dimension on SearchFilters reaches here through the spread once the seed
            // above lists it — and the seed is where it fails to compile until it does.
            ...draft,
        });
        close();
    };

    return (
        <BottomSheet
            title="Filters"
            testId="filter-sheet"
            onClose={close}
            onApply={apply}
        >
            <FilterPanel
                selectedCategoryIds={draft.categoryIds}
                onToggleCategory={toggleCategory}
                maxPrice={draft.maxPrice}
                selectedBrands={draft.brands}
                selectedVendorIds={draft.vendorIds}
                inStockOnly={draft.inStockOnly}
                onMaxPriceChange={setLocalMaxPrice}
                onToggleBrand={toggleBrand}
                onToggleVendor={toggleVendor}
                onInStockOnlyChange={setLocalInStockOnly}
                brands={facets.brands}
                vendors={facets.vendors}
                showInStockOnly={showInStockOnly}
                showHeading={false}
                showCategories={showCategories}
                showVendors={showVendors}
            />
        </BottomSheet>
    );
};
